import React, { useEffect, useReducer, useState } from 'react';
import {
  Button, Dialog, IconButton, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { VARIANTS } from 'shared/constants';
import { NumberEditor, TextAreaEditor } from 'shared/components/data-display/editors';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { addEntry, updateEntry } from 'shared/api/support';
import { dataReducer, DataReducerActions } from 'shared/reducers';
import { TooltipIconButton } from 'shared/components/abstract/interactables';

const initialState = {
  data: {},
  open: false,
  showWarning: false,
};
function entryReducer(state, { action, payload }) {
  switch (action) {
    case 'clear':
      return dataReducer({ ...initialState }, action, payload);
    case 'open':
      return { ...state, open: payload };
    case 'warning':
      return { ...state, showWarning: payload };
    default:
      return dataReducer(state, { action, payload });
  }
}

/**
 * @param {Object} props
 * @param {Object} props.initialState
 */
function EntryStateManagement(props) {
  const [state, dispatch] = useReducer(entryReducer, props);

  const clear = () => {
    dispatch({ action: DataReducerActions.clear });
  };
  const close = () => dispatch({ action: 'open', payload: false });
  const open = () => dispatch({ action: 'open', payload: true });
  const reset = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      dispatch({ action: 'warning', payload: true });
      return;
    }
    clear();
  };
  const set = (payload) => dispatch({ action: DataReducerActions.set, payload });
  const update = (key, value) => dispatch({
    action: DataReducerActions.update,
    payload: { key, value },
  });

  return {
    state, clear, close, open, reset, set, update,
  };
}

const entryEditor = {
  label: 'Text',
  key: 'text',
  editor: TextAreaEditor,
};

const minutesEditor = {
  label: 'Minutes',
  key: 'minutes',
  editor: NumberEditor,
};

/**
 * @param {Object} props
 * @param {string} [props.callToActionText='Save']
 * @param {React.ReactNode} props.children
 * @param {Function} props.btnToClick
 * @param {Object} props.stateManagement
 * @param {Function} props.submitFunc
 * @param {string} props.title
 */
function EntryDialog(props) {
  const {
    children,
    callToActionText = 'Save',
    btnToClick: ButtonToClick,
    stateManagement,
    submitFunc,
    title,
  } = props;

  const { state, clear, reset } = stateManagement;
  const { open, showWarning } = state;

  const [expand, setExpand] = useState(false);
  const expandDialog = () => setExpand(true);
  const shrinkDialog = () => setExpand(false);

  return (
    <>
      <ButtonToClick />
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={reset}
        fullScreen={expand}
      >
        <DialogTitle>
          {title}
          {expand
            ? <TooltipIconButton className="clear-text borderless-icon-btn" icon={AspectRatioIcon} message="Shrink dialog" onClick={shrinkDialog} />
            : <TooltipIconButton className="clear-text borderless-icon-btn" icon={AspectRatioIcon} message="Fully expand dialog" onClick={expandDialog} />}
        </DialogTitle>
        <DialogContent>
          <Grid sx={{ m: '10px 0' }}>
            {children}
          </Grid>
          {showWarning
            && (
              <Alert severity="error" sx={{ mb: 2 }}>
                  {`Must click ${callToActionText} or Cancel to close dialog before continuing`}
              </Alert>
            )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button
              variant="outlined"
              onClick={clear}
            >
              Cancel
            </Button>
            <Button
              variant={VARIANTS.contained}
              onClick={submitFunc}
            >
              {callToActionText}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * @param {Object} props
 * @param {Function} props.reload
 * @param {number} props.ticketId
 * @param {boolean} props.isMobile
 */
export function NewEntryDialog(props) {
  const { user } = useAuth();
  const { request } = useRequest();
  const notification = useNotification();

  const {
    reload,
    ticketId,
    isMobile,
  } = props;

  const management = EntryStateManagement(initialState);
  const { state: { data, updatedData }, clear, update } = management;

  const submit = async () => {
    const params = {
      ticket_id: ticketId,
      text: updatedData.text,
      username: user.username,
      minutes: updatedData.minutes,
    };
    const result = await request(addEntry(params));
    if (!result) {
      return;
    }
    notification.pushSuccess('Ticket entry created');
    clear();
    reload();
  };
  const btnToClick = () => (
    <Button
      id="new-entry-button"
      variant={VARIANTS.contained}
      onClick={management.open}
      sx={isMobile ? { width: '100%' } : { display: 'flex', ml: 'auto' }}
    >
      New Entry
    </Button>
  );

  return (
    <EntryDialog
      callToActionText="Create"
      reload={reload}
      btnToClick={btnToClick}
      submitFunc={submit}
      stateManagement={management}
      title="New Entry"
      ticketId={ticketId}
    >
      <DataFormatter
        editing
        data={data}
        descriptions={[entryEditor, minutesEditor]}
        updatedData={updatedData}
        updateValue={update}
      />
    </EntryDialog>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.entry
 * @param {string} props.expireTime
 * @param {Function} props.reload
 * @param {number} props.employeeId
 * @param {number} props.managerId
 */
export function EditEntryDialog(props) {
  const { user } = useAuth();
  const { isLoading, request } = useRequest();
  const notification = useNotification();
  const {
    entry,
    expireTime,
    reload,
    employeeId,
    managerId,
  } = props;

  const management = EntryStateManagement(initialState);
  const {
    state: { data, updatedData }, close, set, update,
  } = management;
  const {
    minutes, username, text, ticket_id: ticketId,
  } = data;

  useEffect(() => {
    if (!isLoading && entry.text !== text) {
      set(entry);
    }
  }, [entry, isLoading, set, text]);

  const btnToClick = () => (
    <Tooltip title={`Edit entry available until ${expireTime}`}>
      <IconButton color="primary" onClick={management.open}>
        <EditIcon />
      </IconButton>
    </Tooltip>
  );

  const submit = async () => {
    const params = {
      ticket_id: ticketId,
      text: updatedData.text || entry.text,
      username: user.username,
      minutes: entry.minutes,
      created_at: entry.created_at,
      employee_id: employeeId,
      manager_id: managerId,
    };

    const result = await request(updateEntry(entry.id, params));
    if (!result) {
      return;
    }
    notification.pushSuccess('Ticket entry updated');
    close();
    reload();
  };

  return (
    <EntryDialog
      stateManagement={management}
      btnToClick={btnToClick}
      submitFunc={submit}
      title="Edit Entry"
      ticketId={ticketId}
    >
      <div className="flex-between">
        <DialogContentText>
          {`Minutes: ${minutes}`}
        </DialogContentText>
        <DialogContentText>
          {`Created By: ${username}`}
        </DialogContentText>
      </div>
      <DataFormatter
        editing
        data={data}
        descriptions={[entryEditor]}
        updatedData={updatedData}
        updateValue={update}
      />
    </EntryDialog>
  );
}
